import type { ChatMentionsMenuItemType } from '@terra/ui-design-system';
import type { HttpMentionData } from '@terra/shared-dto-chat-types';
import { ArrowDownToDotIcon, SentIcon } from '@terra/ui-icons';
import { createElement } from 'react';
import { normalizeHttpHeaders } from '@terra/shared-utils';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from './formatHttpForPanel';

/**
 * Create an HTTP request mention item for use in chat
 */
export function createHttpRequestMention({
  id,
  method,
  url,
  headers,
  rawHeaders,
  body,
  httpVersion = '1.1',
}: {
  id: string;
  method: string;
  url: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  httpVersion?: string;
}): ChatMentionsMenuItemType {
  const normalizedHeaders: Record<string, string> = Object.fromEntries(
    Object.entries(normalizeHttpHeaders(headers)).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(', ') : value,
    ]),
  );
  let displayPath = url;

  try {
    const parsed = new URL(url);

    displayPath = parsed.pathname + parsed.search;
    if (displayPath.length > 40) {
      displayPath = displayPath.slice(0, 37) + '...';
    }
  } catch {
    if (displayPath.length > 40) {
      displayPath = displayPath.slice(0, 37) + '...';
    }
  }

  const label = `${method} ${displayPath}`;
  const rawText = formatHttpRequestRaw({
    method,
    url,
    headers,
    rawHeaders,
    body,
    httpVersion,
  });
  const httpData: HttpMentionData = {
    method,
    url,
    headers: normalizedHeaders,
    body,
    contentType:
      normalizedHeaders['content-type'] || normalizedHeaders['Content-Type'],
    rawText,
    httpVersion,
  };
  const instanceId = crypto.randomUUID();

  return {
    id: `httpRequest-${id}-${instanceId}`,
    label,
    icon: createElement(SentIcon, { size: 12 }),
    type: 'child',
    mentionEntity: {
      entityType: 'HttpRequest',
      entityId: `${id}-request-${instanceId}`,
      label,
      extraData: { httpRequest: httpData },
    },
  };
}

/**
 * Create an HTTP response mention item for use in chat
 */
export function createHttpResponseMention({
  id,
  method,
  url,
  statusCode,
  statusText,
  headers,
  rawHeaders,
  body,
  httpVersion = '1.1',
}: {
  id: string;
  method: string;
  url: string;
  statusCode: number;
  statusText?: string;
  headers: Record<string, string | string[]>;
  rawHeaders?: string[];
  body?: string;
  httpVersion?: string;
}): ChatMentionsMenuItemType {
  const normalizedHeaders: Record<string, string> = Object.fromEntries(
    Object.entries(normalizeHttpHeaders(headers)).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.join(', ') : value,
    ]),
  );
  let displayPath = url;

  try {
    const parsed = new URL(url);

    displayPath = parsed.pathname;
    if (displayPath.length > 30) {
      displayPath = displayPath.slice(0, 27) + '...';
    }
  } catch {
    if (displayPath.length > 30) {
      displayPath = displayPath.slice(0, 27) + '...';
    }
  }

  const label = `${statusCode} ${method} ${displayPath}`;
  const rawText = formatHttpResponseRaw({
    status: statusCode,
    statusMessage: statusText || '',
    headers,
    rawHeaders,
    body,
    httpVersion,
  });
  const httpData: HttpMentionData = {
    method,
    url,
    statusCode,
    statusText,
    headers: normalizedHeaders,
    body,
    contentType:
      normalizedHeaders['content-type'] || normalizedHeaders['Content-Type'],
    rawText,
    httpVersion,
  };
  const instanceId = crypto.randomUUID();

  return {
    id: `httpResponse-${id}-${instanceId}`,
    label,
    icon: createElement(ArrowDownToDotIcon, { size: 12 }),
    type: 'child',
    mentionEntity: {
      entityType: 'HttpResponse',
      entityId: `${id}-response-${instanceId}`,
      label,
      extraData: { httpRequest: httpData },
    },
  };
}
